import { computed, ref, watch } from 'vue'
import { defineStore } from 'pinia'
import type { Comic } from '@tanko/shared'
import { useLibraryStore } from './library'
import { useCloudLibraryStore } from './cloud-library'
import { filterComics } from '../lib/comic-filter'
import { clearSelection, countWithin, pruneSelection, selectAll, selectRange, toggleSelection } from '../lib/comic-selection'
import { mergeLibraryEntries, type LibraryEntry } from '../lib/library-entries'
import { presentsComic, privateCategoriesOf, privateCategoriesOfCollection, privateCategoryIds as unionPrivateIds } from '../lib/private-categories'
import { useLibraryDisplay } from '../lib/library-preferences'
import { foldCollections, presentedCellIds, type ShelfItem } from '../lib/shelf-items'

/**
 * 書庫的單一清單。
 *
 * 兩個來源仍然各自存在——本機那一份來自 IndexedDB、雲端那一份來自 D1——但畫面上只有
 * 一份清單。這裡負責把它們合起來，並持有唯一一份搜尋字串與分類選擇。
 *
 * 本機清單來自 IndexedDB，永遠先到；雲端清單還沒回來時合併結果就只有本機那些，因此
 * 開啟書庫不會先空白再跳出來。
 */
export const useLibraryEntriesStore = defineStore('library-entries', () => {
  const local = useLibraryStore()
  const cloud = useCloudLibraryStore()
  const display = useLibraryDisplay()

  const query = ref('')
  const selectedCategories = ref<string[]>([])

  // 雲端那一邊的篩選在伺服器端做，因此把同一份條件寫過去；本機那一半在前端篩。
  watch(query, value => { cloud.query = value })
  watch(selectedCategories, value => { cloud.selectedCategories = [...value] }, { deep: true })

  /**
   * 一本本機作品現在讀不讀得出來。三個條件缺一不可：索引完成（頁數與尺寸才確定）、
   * 檔案還在、來源這次工作階段已經授權。
   */
  function locallyReadable(comic: Comic): boolean {
    return comic.indexed && !local.missing.has(comic.id) && local.isAuthorized(comic.libraryId)
  }

  const localMatches = computed(() => filterComics(local.comics, local.assignments, selectedCategories.value, query.value))
  const merged = computed<LibraryEntry[]>(() => mergeLibraryEntries(
    cloud.comics, localMatches.value, locallyReadable, display.sortOrder.value,
  ))

  /**
   * 哪些分類是隱私分類：雲端與本機鏡像的聯集。只看雲端的話，書庫剛開啟（本機作品已畫出來、
   * 雲端還沒回來）與雲端連不上這兩個時刻集合是空的，等於完全沒生效。
   */
  const privateCategoryIds = computed(() => unionPrivateIds({
    cloudCategories: cloud.categories, mirrorCategories: local.categories,
  }))

  /** 這一格屬於哪些隱私分類。同樣取聯集：雲端名單還沒回來時，本機那一份的指派就藏得住。 */
  function privateCategoriesFor(entry: LibraryEntry): string[] {
    return privateCategoriesOf({
      cloudComicId: entry.comic.cloudComicId,
      localComicId: entry.local?.id ?? null,
      privateCategoryIds: privateCategoryIds.value,
      cloudMembers: cloud.privateMembers,
      mirrorAssignments: local.assignments,
    })
  }

  /** 隱私分類的作品不列在預設清單裡；判斷只寫在 private-categories 那一個純函式。 */
  const entries = computed<LibraryEntry[]>(() => merged.value.filter(entry => presentsComic({
    categoryIds: privateCategoriesFor(entry),
    privateCategoryIds: privateCategoryIds.value,
    selectedCategoryIds: selectedCategories.value,
  })))

  /**
   * 書架上的格子：集合成員折進集合格，其餘作品各自一格。`entries` 仍是作品清單（含折
   * 進去的成員），批次動作與備份佇列照舊吃它；只有書架與列表的渲染層改吃 `shelf`。
   */
  const shelf = computed<ShelfItem[]>(() => foldCollections(entries.value, cloud.collections, {
    query: query.value, selectedCategoryIds: selectedCategories.value, privateCategoryIds: privateCategoryIds.value,
    localProgress: local.progress, order: display.sortOrder.value,
  }))
  /** 目前呈現的集合格，供「加入集合」對話框列出。 */
  const collections = computed(() => cloud.collections)

  const ready = computed(() => local.ready)
  const filtering = computed(() => !!query.value.trim() || !!selectedCategories.value.length)

  /*
    選取建立在統一識別碼上，因此不再有「本地的選取」與「雲端的選取」兩份，也不會因為
    重新整理清單而被清掉——修剪的判準是「這個識別碼還在不在清單裡」，而已註冊作品的
    識別碼在任何一次重新整理之間都是同一個。
  */
  const selectionMode = ref(false)
  const selection = ref<ReadonlySet<string>>(new Set())
  /** shift 範圍選取的錨點：上一次單獨點選的那一列。 */
  const selectionAnchor = ref<string>()
  // 每一格的識別碼：全選、範圍選取與修剪的依據。集合格是一格，不展開成成員。
  const presentedIds = computed(() => presentedCellIds(shelf.value))
  const selectedCount = computed(() => selection.value.size)
  const selectedInFilter = computed(() => countWithin(selection.value, presentedIds.value))
  /** 選取中的格子，依呈現順序。批次動作由此分流成作品與集合兩段。 */
  const selectedCells = computed(() => shelf.value.filter(item => selection.value.has(item.id)))
  const selectedEntries = computed(() => selectedCells.value.flatMap(item => item.kind === 'comic' ? [item.entry] : []))
  /** 選取中的集合。批次分類指派給它們自己，批次刪除是連同成員刪除。 */
  const selectedCollections = computed(() => selectedCells.value.flatMap(item => item.kind === 'collection' ? [item.collection] : []))
  /** 改名與備份只作用在作品格；選取裡有幾個集合被跳過，動作完成後要講出來。 */
  const skippedCollectionCount = computed(() => selectedCollections.value.length)
  /**
   * 選取中每一本要交給動作的那一筆作品記錄。
   *
   * 有本機那一份就用它：它同時帶著本機識別碼與雲端識別碼，因此寫雲端與更新本機鏡像
   * 都做得到；只有雲端的那些就用雲端那一份。
   */
  const selectedComics = computed(() => selectedEntries.value.map(entry => entry.local ?? entry.comic))
  /** 選取中這台裝置備份得了的那些：有本機檔案，而且還沒備份完成。 */
  const backupTargets = computed(() => selectedEntries.value.flatMap(entry => entry.local && !entry.backedUp ? [entry.local] : []))
  /** 選取中每一本都在這台裝置上時，依資料夾路徑重新推導標題才有意義。 */
  const allSelectedAreLocal = computed(() => !!selectedEntries.value.length && selectedEntries.value.every(entry => !!entry.local))

  watch(presentedIds, ids => {
    if (!selection.value.size) return
    selection.value = pruneSelection(selection.value, new Set(ids))
  })

  function enterSelectionMode() { selectionMode.value = true }
  function exitSelectionMode() {
    selectionMode.value = false
    selection.value = clearSelection()
    selectionAnchor.value = undefined
  }
  /** `extend` 為 shift 點擊：從上一次單獨點選的那一列連選到這一列。 */
  function toggleSelected(id: string, extend = false) {
    const anchor = selectionAnchor.value
    if (extend && anchor && anchor !== id) {
      selection.value = selectRange(selection.value, presentedIds.value, anchor, id)
      return
    }
    selection.value = toggleSelection(selection.value, id)
    selectionAnchor.value = id
  }
  /** 全選只加入目前篩選結果，不移除落在篩選之外的既有選取。 */
  function selectAllFiltered() {
    selection.value = selectAll(selection.value, presentedIds.value)
  }
  function clearSelected() { selection.value = clearSelection(); selectionAnchor.value = undefined }
  function dropFromSelection(ids: readonly string[]) {
    const removed = new Set(ids)
    selection.value = new Set([...selection.value].filter(id => !removed.has(id)))
  }

  function clearFilters() {
    query.value = ''
    selectedCategories.value = []
  }

  async function initialize(): Promise<void> {
    await Promise.all([local.initialize(), cloud.initialize()])
  }

  async function refresh(): Promise<void> {
    await Promise.all([local.refresh(), cloud.refresh(true)])
  }

  return {
    query, selectedCategories, entries, shelf, collections, ready, filtering, clearFilters, initialize, refresh, locallyReadable,
    selectionMode, selection, selectedCount, selectedInFilter, selectedCells, selectedEntries, selectedComics,
    selectedCollections, skippedCollectionCount, backupTargets, allSelectedAreLocal,
    enterSelectionMode, exitSelectionMode, toggleSelected, selectAllFiltered, clearSelected, dropFromSelection,
  }
})
